import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Plus, Search, X } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APPROVER_ROLES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Panel, DataTable, Fld, StatusPill, DecisionDialog, DecisionCell, money, inputCls } from "./shared";

export const LIQ_SECTIONS = [
  ["Receipted", ["FRT Collect/Additional CA", "CBW Charges", "Breakbulk Fees", "Import Proc. Fee",
    "Releasing Duty Free", "CTF", "BD Fee Comeback Copy", "DG Fee CNIU"]],
  ["Unreceipted", ["WHSE/BOC/Xerox/NF", "BL Nature", "Others"]],
];
const ALL = LIQ_SECTIONS.flatMap(([, cats]) => cats);
const blankAmounts = () => Object.fromEntries(ALL.map((c) => [c, ""]));
const slug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const sum = (amts, cats) => cats.reduce((s, c) => s + (parseFloat(amts[c]) || 0), 0);
const extrasSum = (extras, section) => extras.filter((e) => !section || e.section === section).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
export const liqTotal = (amts, extras) => sum(amts, ALL) + extrasSum(extras);
export const lineLabel = (it) => (it.category === "Others" ? `Others: ${it.specify}` : it.category === "Additional" ? it.specify : it.category);
export const buildLines = (amts, specify, extras) => [
  ...ALL.map((c) => ({ category: c, amount: parseFloat(amts[c]) || 0, specify: c === "Others" ? specify : "" })),
  ...extras.map((e) => ({ category: "Additional", section: e.section, specify: e.specify, amount: parseFloat(e.amount) || 0 })),
];

function ExtraLines({ title, extras, setExtras }) {
  const key = title.toLowerCase();
  const update = (id, patch) => setExtras(extras.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  return (
    <>
      {extras.filter((e) => e.section === title).map((e, i) => (
        <div key={e.id} className="flex items-center gap-2">
          <Input data-testid={`liq-extra-desc-${key}-${i}`} value={e.specify} onChange={(ev) => update(e.id, { specify: ev.target.value })}
                 placeholder="Item description" className={`flex-1 ${inputCls}`} />
          <Input data-testid={`liq-extra-amount-${key}-${i}`} type="number" min="0" value={e.amount} placeholder="0.00"
                 onChange={(ev) => update(e.id, { amount: ev.target.value })} className={`w-32 text-right font-mono-ops ${inputCls}`} />
          <button type="button" data-testid={`liq-extra-remove-${key}-${i}`} onClick={() => setExtras(extras.filter((x) => x.id !== e.id))}
                  className="text-slate-500 hover:text-rose-300 transition-colors" aria-label="Remove item"><X size={16} /></button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" data-testid={`liq-add-item-${key}`}
              onClick={() => setExtras([...extras, { id: Date.now() + Math.random(), section: title, specify: "", amount: "" }])}
              className="text-sky-300 hover:text-sky-200 hover:bg-slate-800 px-2"><Plus size={14} className="mr-1" /> Add item</Button>
    </>
  );
}

export function SectionLines({ title, cats, amts, setAmts, specify, setSpecify, extras, setExtras }) {
  return (
    <div data-testid={`liq-section-${title.toLowerCase()}`} className="bg-slate-950/50 border border-slate-800 rounded-md p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="font-display font-bold">{title}</div>
        <div className="font-mono-ops text-sm text-slate-300" data-testid={`liq-subtotal-${title.toLowerCase()}`}>{money(sum(amts, cats) + extrasSum(extras, title))}</div>
      </div>
      <div className="space-y-2">
        {cats.map((c) => (
          <div key={c} className="flex items-center gap-2">
            {c === "Others" ? (
              <Input data-testid="liq-others-specify" value={specify} onChange={(e) => setSpecify(e.target.value)}
                     placeholder="Others (specify)" className={`flex-1 ${inputCls}`} />
            ) : <span className="flex-1 text-sm text-slate-300">{c}</span>}
            <Input data-testid={`liq-amount-${slug(c)}`} type="number" min="0" value={amts[c]} placeholder="0.00"
                   onChange={(e) => setAmts({ ...amts, [c]: e.target.value })} className={`w-32 text-right font-mono-ops ${inputCls}`} />
          </div>
        ))}
        <ExtraLines title={title} extras={extras} setExtras={setExtras} />
      </div>
    </div>
  );
}

function LiquidationForm({ funds, onSaved, initialFund }) {
  const [fundId, setFundId] = useState(initialFund || "");
  const [amts, setAmts] = useState(blankAmounts());
  const [specify, setSpecify] = useState("");
  const [notes, setNotes] = useState("");
  const [extras, setExtras] = useState([]);
  const [busy, setBusy] = useState(false);
  const fund = funds.find((x) => x.id === fundId);
  const total = liqTotal(amts, extras);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/liquidations", {
        fund_request_id: fundId, notes, lines: buildLines(amts, specify, extras),
      });
      toast.success(`${data.number} submitted for review`);
      setFundId(""); setAmts(blankAmounts()); setSpecify(""); setNotes(""); setExtras([]); onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <Panel title="Liquidate an operational fund" subtitle="Record actual expenses against an approved fund. Finance Manager or Admin reviews it.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Fld label="Approved fund">
          <Select value={fundId} onValueChange={setFundId}>
            <SelectTrigger data-testid="select-liquidation-fund" className={inputCls}><SelectValue placeholder="Choose an approved fund" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
              {funds.map((x) => <SelectItem key={x.id} value={x.id}>{x.number}{x.shipment_number ? ` · ${x.shipment_number}` : ""} · {x.purpose} · {money(x.amount)}</SelectItem>)}
            </SelectContent>
          </Select>
        </Fld>
        <Fld label="Notes"><Input data-testid="input-liquidation-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} /></Fld>
      </div>
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {LIQ_SECTIONS.map(([title, cats]) => (
          <SectionLines key={title} title={title} cats={cats} amts={amts} setAmts={setAmts} specify={specify} setSpecify={setSpecify} extras={extras} setExtras={setExtras} />
        ))}
      </div>
      <div className="mt-3 flex justify-end">
        <div className="font-mono-ops text-sm text-slate-400">
          Spent <span data-testid="liq-total" className="text-slate-100">{money(total)}</span>
          {fund && <> of {money(fund.amount)} · Balance <span data-testid="liq-variance" className={fund.amount - total < 0 ? "text-rose-300" : "text-emerald-300"}>{money(fund.amount - total)}</span></>}
        </div>
      </div>
      <Button data-testid="button-submit-liquidation" onClick={save} disabled={busy || !fundId} className="mt-4 bg-blue-600 hover:bg-blue-500">
        {busy && <Loader2 size={14} className="mr-2 animate-spin" />} Submit liquidation
      </Button>
    </Panel>
  );
}

export default function LiquidationTab({ onChanged }) {
  const { user } = useAuth();
  const canApprove = APPROVER_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [funds, setFunds] = useState([]);
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const load = async () => {
    try {
      const [l, f] = await Promise.all([api.get("/finance/liquidations"), api.get("/finance/fund-requests")]);
      const taken = new Set(l.data.filter((x) => x.status !== "Rejected").map((x) => x.fund_request_id));
      setRows(l.data); setFunds(f.data.filter((x) => x.status === "Approved" && !taken.has(x.id)));
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChanged(); };
  const n = q.toLowerCase();
  const shown = rows.filter((r) => !n || [r.number, r.fund_request_number, r.shipment_number, r.fund_purpose].some((v) => v?.toLowerCase().includes(n)));

  const columns = [
    { key: "number", label: "No.", mono: true },
    { key: "shipment_number", label: "Shipment #", mono: true },
    { key: "fund_request_number", label: "Fund", mono: true },
    { key: "fund_purpose", label: "Purpose" },
    { key: "fund_amount", label: "Fund amount", right: true, render: (r) => money(r.fund_amount) },
    { key: "receipted_total", label: "Receipted", right: true, render: (r) => money(r.receipted_total) },
    { key: "unreceipted_total", label: "Unreceipted", right: true, render: (r) => money(r.unreceipted_total) },
    { key: "total", label: "Total spent", right: true, render: (r) => money(r.total) },
    { key: "variance", label: "Balance", right: true, render: (r) => <span className={r.variance < 0 ? "text-rose-300" : "text-emerald-300"}>{money(r.variance)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} testid={`status-${r.number}`} /> },
    { key: "decision", label: "Reviewed by", render: (r) => <DecisionCell r={r} /> },
    { key: "action", label: "", render: (r) => (canApprove && r.status === "Pending" ? (
      <DecisionDialog kind="liquidations" record={r} onDone={refresh}>
        {LIQ_SECTIONS.map(([title]) => {
          const lines = (r.items || []).filter((it) => it.section === title);
          return lines.length ? (
            <div key={title}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{title}</div>
              {lines.map((it, i) => (
                <div key={i} className="flex justify-between gap-4 border-b border-slate-800 pb-1">
                  <span>{lineLabel(it)}</span><span className="font-mono-ops">{money(it.amount)}</span>
                </div>
              ))}
            </div>
          ) : null;
        })}
        <div className="flex justify-between font-mono-ops"><span className="text-slate-500">Fund {money(r.fund_amount)}</span><span>Balance {money(r.variance)}</span></div>
      </DecisionDialog>) : null) },
  ];

  return (
    <div className="space-y-6">
      <LiquidationForm funds={funds} onSaved={refresh} initialFund={params.get("fund")} />
      <Panel title="Liquidations" subtitle="Each liquidation carries the shipment number of its cash advance."
             right={
               <div className="relative w-full sm:w-72">
                 <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                 <Input data-testid="input-liquidation-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shipment, fund, liquidation…" className={`pl-9 ${inputCls}`} />
               </div>
             }>
        <DataTable testid="liquidations-table" columns={columns} rows={shown} empty="No liquidations yet." />
      </Panel>
    </div>
  );
}
